import { dayMs, hourMs, minuteMs, secondMs } from "../timeConstants.js";

// Shared types for time formatting options
interface BaseTimeOptions {
    minLabel?: string;
    monthLabel?: string;
    nowMs?: number;
}

interface RelativeTimeOptions extends BaseTimeOptions {
    hrLabel?: string;
    dayLabel?: string;
}

interface TimeFormatResult {
    label: string;
    isDate: boolean;
}

/**
 * Returns a string representing the time elapsed (assuming 0 is the start time) like a timer.
 *
 * Format progression: "MM:SS" -> "HH:MM:SS" -> "1d HH:MM:SS"
 */
export function formatTimer(ms: number, dayChar: string = "d"): string {
    if (ms < secondMs) {
        return "00:00";
    }
    const days = Math.floor(ms / dayMs).toString();
    const hours = Math.floor((ms % dayMs) / hourMs)
        .toString()
        .padStart(2, "0");
    const minutes = Math.floor((ms % hourMs) / minuteMs)
        .toString()
        .padStart(2, "0");
    const seconds = Math.floor((ms % minuteMs) / secondMs)
        .toString()
        .padStart(2, "0");

    if (Number(days) !== 0) {
        return `${days}${dayChar} ${hours}:${minutes}:${seconds}`;
    }
    if (Number(hours) !== 0) {
        return `${hours}:${minutes}:${seconds}`;
    }

    return `${minutes}:${seconds}`;
}

/**
 * Formats absolute date with time (eg. "Aug 7, 12:00 AM")
 */
function formatAbsoluteDate(timestampMs: number, monthLabel?: string, nowMs?: number): string {
    const date = new Date(timestampMs);
    const currentYear = new Date(nowMs ?? Date.now()).getFullYear();
    const dateYear = date.getFullYear();

    const timeString = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });

    if (monthLabel) {
        if (dateYear === currentYear) {
            return `${monthLabel} ${date.getDate()}, ${timeString}`;
        }
        return `${monthLabel} ${date.getDate()}, ${dateYear}, ${timeString}`;
    }

    const monthString = date.toLocaleDateString("en-US", { month: "short" });
    if (dateYear === currentYear) {
        return `${monthString} ${date.getDate()}, ${timeString}`;
    }
    return `${monthString} ${date.getDate()}, ${dateYear}, ${timeString}`;
}

/**
 * Helper to add plural suffix when count is not 1
 */
function pluralize(count: number, label: string): string {
    return `${label}${count !== 1 ? "s" : ""}`;
}

/**
 * Handles common minute/hour formatting logic shared between time functions
 */
function formatMinutesAndHours(diffMs: number, minLabel: string = "min"): TimeFormatResult | null {
    if (diffMs < minuteMs) {
        return {
            label: `< 1 ${minLabel}`,
            isDate: false,
        };
    }

    if (diffMs < hourMs) {
        const minutes = Math.floor(diffMs / minuteMs);
        return {
            label: `${minutes} ${minLabel}`,
            isDate: false,
        };
    }

    return null;
}

/**
 * Formats a recent timestamp for displays where time accuracy is important.
 * Quickly transitions from relative time to absolute time.
 *
 * Format progression:
 * - Less than 1 minute ago: "< 1 min"
 * - Less than 1 hour ago: "2 min"
 * - Greater than 1 hour ago: "HH:MM AM" or "MMM DD, HH:MM AM"
 */
export function formatPreciseTime(timestampMs: number, options: BaseTimeOptions = {}): TimeFormatResult {
    const { minLabel = "min", monthLabel, nowMs } = options;
    const now = nowMs ?? Date.now();
    const diffMs = now - timestampMs;

    const shortFormat = formatMinutesAndHours(diffMs, minLabel);
    if (shortFormat) {
        return shortFormat;
    }

    return {
        label: formatAbsoluteDate(timestampMs, monthLabel, now),
        isDate: true,
    };
}

/**
 * Formats a timestamp as "time ago" display, staying in relative format longer.
 *
 * Format progression:
 * - Less than 1 minute ago: "< 1 min"
 * - Less than 1 hour ago: "2 min"
 * - Less than 1 day ago: "2 hrs"
 * - Less than 2 days ago: "1 day"
 * - Greater than 2 days ago: "MMM DD, HH:MM AM"
 */
export function formatRelativeTime(timestampMs: number, options: RelativeTimeOptions = {}): TimeFormatResult {
    const { minLabel = "min", hrLabel = "hr", dayLabel = "day", monthLabel, nowMs } = options;
    const now = nowMs ?? Date.now();
    const diffMs = now - timestampMs;

    const shortFormat = formatMinutesAndHours(diffMs, minLabel);
    if (shortFormat) {
        return shortFormat;
    }

    if (diffMs < dayMs) {
        const hours = Math.floor(diffMs / hourMs);
        return {
            label: `${hours} ${pluralize(hours, hrLabel)}`,
            isDate: false,
        };
    }

    if (diffMs < 2 * dayMs) {
        const days = Math.floor(diffMs / dayMs);
        return {
            label: `${days} ${pluralize(days, dayLabel)}`,
            isDate: false,
        };
    }

    return {
        label: formatAbsoluteDate(timestampMs, monthLabel, now),
        isDate: true,
    };
}

/**
 * Check if a date is valid.
 */
export function isValidDate(date: any): boolean {
    if (!date || Object.prototype.toString.call(date) !== "[object Date]") {
        return false;
    }
    if (Number.isNaN(date.getTime())) {
        return false;
    }
    return true;
}

export interface FormatDurationOptions {
    includeSecondsWithMinutes?: boolean;
    hourLabel?: string;
    minuteLabel?: string;
    secondLabel?: string;
}

/**
 * Formats milliseconds to a human-readable duration string.
 *
 * Outputs things like "2hr 14min", "2min 15sec", or "10sec"
 *
 * Unit labels can be customized for localization by passing translated labels.
 */
export function formatTimeAsHoursMinutesAndSeconds(ms: number, options: FormatDurationOptions = {}): string {
    const { includeSecondsWithMinutes = true, hourLabel = "hr", minuteLabel = "min", secondLabel = "sec" } = options;

    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
        return `${hours}${hourLabel} ${minutes % 60}${minuteLabel}`;
    }
    if (minutes > 0) {
        return includeSecondsWithMinutes
            ? `${minutes}${minuteLabel} ${seconds % 60}${secondLabel}`
            : `${minutes}${minuteLabel}`;
    }
    return `${seconds}${secondLabel}`;
}
